import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ListChecks, Download, AlertTriangle, Loader2, CheckCircle2, XCircle, Filter,
  RefreshCw,
} from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';

import PeriodPicker, { defaultDashboardRange } from '../../components/mec/PeriodPicker';
import ShopFilter from '../../components/mec/ShopFilter';
import MatchRow from '../../components/mec/drilldown/MatchRow';
import { labelFor } from '../../components/mec/mecLabels';

import {
  listRouting, getClose, listMatches, confirmMatch, rejectMatch,
} from '../../api/mec';
import { formatMoney } from '../../utils/formatters';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../../components/mec/csvHelpers';

// Tier 2 Review (§7) — global queue of pending_review mec_match rows
// across selected shops + period. Fans out across multiple close runs in
// parallel; aggregates results client-side.

const SCORE_BANDS = [
  { key: 'all', label: 'All scores', filter: () => true },
  { key: 'high', label: 'Score ≥ 0.95', filter: (s) => s != null && s >= 0.95 },
  { key: 'mid',  label: '0.80 – 0.94', filter: (s) => s != null && s >= 0.80 && s < 0.95 },
  { key: 'low',  label: '< 0.80',      filter: (s) => s == null || s < 0.80 },
];

const SORT_FIELDS = [
  { key: 'score',    label: 'Score' },
  { key: 'amount',   label: 'Amount' },
  { key: 'date',     label: 'Date' },
  { key: 'vendor',   label: 'Vendor' },
];

export default function Tier2Review() {
  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingError, setRoutingError] = useState(null);

  const defaultRange = useMemo(() => defaultDashboardRange(), []);
  const [periodStart, setPeriodStart] = useState(defaultRange.start);
  const [periodEnd, setPeriodEnd] = useState(defaultRange.end);
  const [selectedIds, setSelectedIds] = useState([]);

  const [domain, setDomain] = useState('parts'); // 'parts' | 'sublet'

  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState(null);

  const [scoreBand, setScoreBand] = useState('all');
  const [sortField, setSortField] = useState('score');
  const [sortDir, setSortDir] = useState('desc');

  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // 'confirm' | 'reject' | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  // Load shop routing.
  useEffect(() => {
    let alive = true;
    setRoutingLoading(true);
    listRouting()
      .then((data) => {
        if (!alive) return;
        const shops = (data?.shops || []).filter((s) => s.shop_id !== 7515);
        setRouting(shops);
        setSelectedIds(shops.map((s) => s.shop_id));
      })
      .catch((err) => {
        if (!alive) return;
        setRoutingError(err?.message || 'Failed to load shops');
      })
      .finally(() => alive && setRoutingLoading(false));
    return () => { alive = false; };
  }, []);

  const selectedShops = useMemo(
    () => routing.filter((s) => selectedIds.includes(s.shop_id)),
    [routing, selectedIds]
  );

  // Fetch close runs for each selected shop, then their matches in parallel.
  const reloadMatches = useCallback(async () => {
    if (!selectedShops.length || !periodStart || !periodEnd) {
      setMatches([]);
      return;
    }
    setMatchesLoading(true);
    setMatchesError(null);
    setSelectedMatchIds([]);
    try {
      // Step 1: resolve close_run_id for each shop via GET /close
      const closeRuns = await Promise.allSettled(
        selectedShops.map((s) =>
          getClose(s.shop_id, periodStart, periodEnd)
            .then((data) => ({ shop_id: s.shop_id, shop_name: s.shop_name, close_run_id: data?.close_run_id }))
            .catch(() => ({ shop_id: s.shop_id, shop_name: s.shop_name, close_run_id: null }))
        )
      );
      const validRuns = closeRuns
        .filter((r) => r.status === 'fulfilled' && r.value.close_run_id)
        .map((r) => r.value);

      // Step 2: fetch matches per close_run_id.
      const matchSets = await Promise.allSettled(
        validRuns.map((r) =>
          listMatches(r.close_run_id, domain)
            .then((data) => ({
              ...r,
              matches: (data?.matches || data || []).map((m) => ({
                ...m,
                _shop_name: r.shop_name,
                _shop_id: r.shop_id,
              })),
            }))
            .catch(() => ({ ...r, matches: [] }))
        )
      );
      const all = [];
      for (const r of matchSets) {
        if (r.status === 'fulfilled') {
          for (const m of r.value.matches) {
            // Only Tier 2 / pending_credit_* in pending_review status
            const isTier2 = (
              m.matched_method === 'tier_2_heuristic'
              || m.matched_method === 'pending_credit_warranty_tagged'
              || m.matched_method === 'pending_credit_zero_cost_match'
              || m.matched_method === 'pending_credit_manual'
            );
            if (isTier2 && m.review_status === 'pending_review') {
              all.push(m);
            }
          }
        }
      }
      setMatches(all);
    } catch (err) {
      setMatchesError(err?.message || 'Failed to load review queue');
    } finally {
      setMatchesLoading(false);
    }
  }, [selectedShops, periodStart, periodEnd, domain]);

  useEffect(() => {
    reloadMatches();
  }, [reloadMatches]);

  // Filtered + sorted view.
  const filteredSorted = useMemo(() => {
    const band = SCORE_BANDS.find((b) => b.key === scoreBand) || SCORE_BANDS[0];
    let list = matches.filter((m) => band.filter(m.score));
    list = list.slice();
    list.sort((a, b) => {
      let av, bv;
      switch (sortField) {
        case 'score': av = a.score ?? -1; bv = b.score ?? -1; break;
        case 'amount': av = a.qbo_amount_cents ?? 0; bv = b.qbo_amount_cents ?? 0; break;
        case 'date': av = a.qbo_date || ''; bv = b.qbo_date || ''; break;
        case 'vendor': av = (a.qbo_vendor || '').toLowerCase(); bv = (b.qbo_vendor || '').toLowerCase(); break;
        default: av = 0; bv = 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [matches, scoreBand, sortField, sortDir]);

  const totals = useMemo(() => {
    const parts = matches.filter((m) => !m._is_sublet);
    return {
      count: filteredSorted.length,
      totalCents: filteredSorted.reduce((s, m) => s + (m.qbo_amount_cents || 0), 0),
      allCount: matches.length,
      partsCount: parts.length,
    };
  }, [matches, filteredSorted]);

  const allFilteredSelected = filteredSorted.length > 0
    && filteredSorted.every((m) => selectedMatchIds.includes(m.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedMatchIds([]);
    } else {
      setSelectedMatchIds(filteredSorted.map((m) => m.id));
    }
  };

  const onMatchResolved = (matchId, updated) => {
    setMatches((prev) => prev.filter((m) => m.id !== matchId));
    setSelectedMatchIds((prev) => prev.filter((id) => id !== matchId));
  };

  const runBulkAction = async () => {
    setBulkBusy(true);
    setBulkResult(null);
    const ids = selectedMatchIds.slice();
    const tally = { ok: 0, err: 0 };
    await Promise.all(ids.map(async (id) => {
      try {
        if (bulkAction === 'confirm') await confirmMatch(id);
        else await rejectMatch(id, bulkRejectReason);
        tally.ok += 1;
      } catch {
        tally.err += 1;
      }
    }));
    setBulkBusy(false);
    setBulkResult(tally);
    // Remove resolved from local state
    setMatches((prev) => prev.filter((m) => !ids.includes(m.id)));
    setSelectedMatchIds([]);
    setBulkRejectReason('');
  };

  const exportCsv = () => {
    const csv = rowsToCsv(filteredSorted, [
      { key: '_shop_name', label: 'Shop' },
      { key: 'qbo_vendor', label: 'QBO Vendor' },
      { key: 'qbo_date', label: 'QBO Date' },
      { key: 'qbo_amount', label: 'QBO Amount', accessor: (r) => centsToDollarsRaw(r.qbo_amount_cents) },
      { key: 'qbo_memo', label: 'QBO Memo' },
      { key: 'matched_method', label: 'Method', accessor: (r) => labelFor(r.matched_method) },
      { key: 'matched_po_number', label: 'PO #' },
      { key: 'matched_ro_id', label: 'RO #' },
      { key: 'matched_amount', label: 'Tekmetric Amount', accessor: (r) => centsToDollarsRaw(r.matched_amount_cents) },
      { key: 'amount_delta', label: 'Δ Amount', accessor: (r) => centsToDollarsRaw(r.amount_delta_cents) },
      { key: 'date_delta_days', label: 'Δ Days' },
      { key: 'score', label: 'Score' },
    ]);
    downloadCsv(`mec-tier2-review-${periodStart}-${periodEnd}.csv`, csv);
  };

  return (
    <div>
      <PageHeader
        title="Pending Heuristic Matches"
        description="Tier 2 + pending-credit matches awaiting your review (Mode A queue). Confirm to roll dollars into A/WIP; reject to keep them as leakage."
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Pending Matches' },
        ]}
        actions={
          <>
            <Button
              variant="ghost"
              size="md"
              leftIcon={<RefreshCw size={15} />}
              onClick={reloadMatches}
              disabled={matchesLoading}
            >
              Refresh
            </Button>
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Download size={15} />}
              onClick={exportCsv}
              disabled={filteredSorted.length === 0}
            >
              Export CSV
            </Button>
          </>
        }
      />

      {routingError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-[14px]">{routingError}</div>
          </div>
        </Card>
      )}

      <Card className="mb-5">
        <div className="flex flex-col gap-4">
          <PeriodPicker start={periodStart} end={periodEnd} onChange={(s, e) => {
            setPeriodStart(s);
            setPeriodEnd(e);
          }} />
          <div className="h-px bg-[var(--color-warm-100)] -mx-5" />
          <ShopFilter shops={routing} selectedIds={selectedIds} onChange={setSelectedIds} />
        </div>
      </Card>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)]">
          <Filter size={13} />
          Filter
        </div>

        {/* Score band */}
        <div className="inline-flex items-center bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 gap-0.5 border border-[var(--color-warm-100)]">
          {SCORE_BANDS.map((b) => {
            const active = scoreBand === b.key;
            const count = matches.filter((m) => b.filter(m.score)).length;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => setScoreBand(b.key)}
                className={`
                  px-3 h-7 text-[12px] font-semibold rounded-[var(--radius-full)] transition-colors inline-flex items-center gap-1.5
                  ${active
                    ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                    : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
                `}
              >
                {b.label}
                {count > 0 && (
                  <span className={`text-[10px] mono-nums ${active ? 'opacity-80' : 'opacity-70'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <span className="text-[var(--color-warm-300)]">·</span>

        {/* Domain: parts vs sublet */}
        <div className="inline-flex items-center gap-1.5">
          <span className="text-[11px] text-[var(--color-warm-500)]">Domain:</span>
          {[
            { key: 'parts', label: 'Parts' },
            { key: 'sublet', label: 'Sublet' },
          ].map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDomain(d.key)}
              className={`
                h-7 px-2.5 text-[12px] font-semibold rounded-[var(--radius-full)] transition-colors border
                ${domain === d.key
                  ? 'bg-[var(--color-action-50)] text-[var(--color-action-700)] border-[var(--color-action-300)]'
                  : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
              `}
            >
              {d.label}
            </button>
          ))}
        </div>

        <span className="text-[var(--color-warm-300)]">·</span>

        {/* Sort */}
        <div className="inline-flex items-center gap-1.5">
          <span className="text-[11px] text-[var(--color-warm-500)]">Sort:</span>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="h-7 px-2 text-[12px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-sm)] focus:outline-none"
          >
            {SORT_FIELDS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
            className="h-7 px-2 text-[12px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-sm)] hover:bg-[var(--color-cream-200)]"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Status band */}
      <Card className="mb-4 !p-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 text-[12px] text-[var(--color-warm-600)]">
          <div className="inline-flex items-center gap-1.5">
            <ListChecks size={14} className="text-[var(--color-action-600)]" />
            <span className="font-semibold text-[var(--color-warm-900)] mono-nums">
              {totals.count}
            </span>
            <span>pending match{totals.count === 1 ? '' : 'es'}</span>
          </div>
          <span className="text-[var(--color-warm-300)]">·</span>
          <span className="tabular-nums font-semibold text-[var(--color-warm-800)]">
            {formatMoney(totals.totalCents / 100)} total
          </span>
          <span className="text-[var(--color-warm-300)]">·</span>
          <span>
            <Badge tone="cream" size="xs">Parts {totals.partsCount}</Badge>
          </span>
        </div>

        {selectedMatchIds.length > 0 && (
          <div className="inline-flex items-center gap-2">
            <span className="text-[12px] text-[var(--color-warm-700)] font-semibold">
              {selectedMatchIds.length} selected
            </span>
            <Button
              size="sm"
              variant="accent"
              leftIcon={<CheckCircle2 size={13} />}
              onClick={() => setBulkAction('confirm')}
            >
              Confirm selected
            </Button>
            <Button
              size="sm"
              variant="destructive"
              leftIcon={<XCircle size={13} />}
              onClick={() => setBulkAction('reject')}
            >
              Reject selected
            </Button>
          </div>
        )}
      </Card>

      {matchesError && (
        <Card className="mb-4">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div className="text-[13px]">{matchesError}</div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card padded={false}>
        {matchesLoading ? (
          <div className="flex items-center gap-2 justify-center py-12 text-[12px] text-[var(--color-warm-500)]">
            <Loader2 size={14} className="animate-spin" />
            Loading review queue across {selectedShops.length} shop{selectedShops.length === 1 ? '' : 's'}…
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 size={28} className="mx-auto mb-3 text-[var(--color-recovery-500)]" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              No pending heuristic matches.
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1 max-w-md mx-auto">
              {matches.length > 0
                ? 'No rows match the current score-band filter.'
                : 'Nothing to review for the selected period and shops. Run a close to populate the queue if you expect rows here.'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
                  <Th width={40}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                      className="accent-[var(--color-warm-900)] w-4 h-4 cursor-pointer"
                    />
                  </Th>
                  <Th>Shop / Vendor / Date</Th>
                  <Th align="right">Amount</Th>
                  <Th>Matched To</Th>
                  <Th align="right">Δ $</Th>
                  <Th align="right">Δ Days</Th>
                  <Th align="right">Score</Th>
                  <Th>Method</Th>
                  <Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map((m) => (
                  <MatchRow
                    key={m.id}
                    match={m}
                    showCheckbox
                    checked={selectedMatchIds.includes(m.id)}
                    onCheck={(id, checked) => {
                      setSelectedMatchIds((prev) =>
                        checked ? [...prev, id] : prev.filter((x) => x !== id)
                      );
                    }}
                    onResolved={onMatchResolved}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Bulk action modal */}
      <Modal
        open={bulkAction != null}
        onClose={() => { setBulkAction(null); setBulkResult(null); setBulkRejectReason(''); }}
        title={bulkAction === 'confirm' ? 'Confirm selected matches' : 'Reject selected matches'}
        description={
          bulkAction === 'confirm'
            ? `Roll ${selectedMatchIds.length} match${selectedMatchIds.length === 1 ? '' : 'es'} into A/WIP buckets. This is reversible — Tier 2 matches stay in the audit trail.`
            : `Mark ${selectedMatchIds.length} match${selectedMatchIds.length === 1 ? '' : 'es'} as rejected. These dollars will count as Tier 3 leakage.`
        }
        size="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="md"
              onClick={() => { setBulkAction(null); setBulkResult(null); setBulkRejectReason(''); }}
              disabled={bulkBusy}
            >
              Cancel
            </Button>
            <Button
              variant={bulkAction === 'confirm' ? 'accent' : 'destructive'}
              size="md"
              onClick={runBulkAction}
              loading={bulkBusy}
              leftIcon={bulkAction === 'confirm' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            >
              {bulkAction === 'confirm' ? 'Confirm all' : 'Reject all'}
            </Button>
          </div>
        }
      >
        <div className="px-6 py-5 space-y-3">
          {bulkAction === 'reject' && (
            <div>
              <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">
                Reject reason (optional)
              </label>
              <textarea
                rows={3}
                value={bulkRejectReason}
                onChange={(e) => setBulkRejectReason(e.target.value)}
                placeholder="e.g., wrong vendor, duplicate, return pending…"
                className="w-full px-3 py-2 text-[13px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] focus:outline-none focus:border-[var(--color-warm-400)]"
              />
              <p className="text-[11px] text-[var(--color-warm-500)] mt-1">
                Applied to all {selectedMatchIds.length} match{selectedMatchIds.length === 1 ? '' : 'es'} in this batch.
              </p>
            </div>
          )}
          {bulkResult && (
            <div className={`
              p-3 rounded-[var(--radius-md)] text-[12px]
              ${bulkResult.err === 0
                ? 'bg-[var(--color-success-50)] border border-[var(--color-success-100)] text-[var(--color-success-700)]'
                : 'bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[var(--color-danger-700)]'}
            `}>
              {bulkResult.ok} succeeded, {bulkResult.err} failed.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function Th({ children, align = 'left', width }) {
  return (
    <th
      style={width ? { width: `${width}px` } : undefined}
      className={`
        px-3 py-2.5 text-[10px] font-bold uppercase tracking-[1px]
        text-[var(--color-warm-500)]
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  );
}

